import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function RelatedComicCard({ comic }: { comic: Comic }) {
  const [imgError, setImgError] = useState(false);
  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block"
    >
      <div className="relative overflow-hidden rounded-lg bg-background-100">
        <div className="aspect-[3/4] w-full overflow-hidden">
          {imgError ? (
            <div className="w-full h-full flex items-center justify-center bg-background-200 text-foreground-400">
              <div className="w-10 h-10 flex items-center justify-center">
                <i className="ri-image-line text-2xl"></i>
              </div>
            </div>
          ) : (
            <img
              src={comic.coverImage}
              alt={`${comic.title} - 韩国漫画`}
              title={`${comic.title} - 免费韩国漫画在线阅读`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              onError={() => setImgError(true)}
            />
          )}
        </div>
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-background-950/70 text-background-50">
            {comic.rating}分
          </span>
        </div>
      </div>
      <div className="mt-2 px-0.5">
        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
      </div>
    </Link>
  );
}

export default function ComicDetailPage() {
  const { comicId } = useParams();
  const [comic, setComic] = useState<Comic | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const found = comics.find((c) => c.id === comicId);
    if (found) {
      setComic(found);
      setNotFound(false);
    } else {
      setNotFound(true);
    }
  }, [comicId]);

  if (notFound) {
    return (
      <main className="pt-16">
        <div className="px-4 md:px-6 py-20 text-center">
          <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
            <i className="ri-file-search-line text-3xl"></i>
          </div>
          <h1 className="mt-6 text-xl font-heading font-bold text-foreground-900">漫画未找到</h1>
          <p className="mt-2 text-foreground-500">抱歉，您要找的漫画不存在或已下架</p>
          <Link
            to="/category/all"
            className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            浏览全部漫画
          </Link>
        </div>
      </main>
    );
  }

  if (!comic) {
    return (
      <main className="pt-16">
        <div className="px-4 md:px-6 py-20 text-center">
          <div className="w-10 h-10 mx-auto flex items-center justify-center">
            <i className="ri-loader-4-line text-3xl text-primary-500 animate-spin"></i>
          </div>
          <p className="mt-4 text-foreground-500">加载中...</p>
        </div>
      </main>
    );
  }

  const relatedComics = comics
    .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 4);

  const detailSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': `${comic.title} - 免费韩国漫画在线阅读`,
    'description': comic.description,
    'author': {
      '@type': 'Person',
      'name': comic.author,
    },
    'datePublished': comic.updateDate,
    'dateModified': comic.updateDate,
    'url': `https://www.manhuazhijia.com/comic/${comic.id}`,
    'isPartOf': {
      '@type': 'WebSite',
      'name': '韩漫之家',
      'url': 'https://www.manhuazhijia.com/',
    },
    'about': {
      '@type': 'Thing',
      'name': `${comic.category}韩国漫画`,
    },
    'breadcrumb': {
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': '首页',
          'item': 'https://www.manhuazhijia.com/',
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': `${comic.category}漫画`,
          'item': `https://www.manhuazhijia.com/category/${comic.categorySlug}`,
        },
        {
          '@type': 'ListItem',
          'position': 3,
          'name': comic.title,
          'item': `https://www.manhuazhijia.com/comic/${comic.id}`,
        },
      ],
    },
  };

  return (
    <>
      <head>
        <title>{`${comic.title} - 免费韩国漫画下拉式在线阅读 - 韩漫之家`}</title>
        <meta name="description" content={`${comic.title} - ${comic.description.slice(0, 120)}... 韩漫之家提供${comic.title}高清下拉式在线阅读，作者${comic.author}，${comic.category}类型漫画。`} />
        <meta name="keywords" content={`${comic.title},${comic.category}韩漫,韩国漫画,下拉式在线阅读,高清漫画,韩漫之家`} />
        <link rel="canonical" href={`https://www.manhuazhijia.com/comic/${comic.id}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${comic.title} - 免费韩国漫画下拉式在线阅读 - 韩漫之家`} />
        <meta property="og:description" content={comic.description} />
        <meta property="og:url" content={`https://www.manhuazhijia.com/comic/${comic.id}`} />
        <script type="application/ld+json">
          {JSON.stringify(detailSchema)}
        </script>
      </head>

      <main className="pt-16">
        <div className="px-4 md:px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-1.5 text-sm mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
              <span className="text-foreground-300">/</span>
              <Link to={`/category/${comic.categorySlug}`} className="text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap">{comic.category}漫画</Link>
              <span className="text-foreground-300">/</span>
              <span className="text-foreground-900 font-medium truncate">{comic.title}</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  {imgError ? (
                    <div className="w-full h-full flex items-center justify-center bg-background-200 text-foreground-400">
                      <div className="w-16 h-16 flex items-center justify-center">
                        <i className="ri-image-line text-4xl"></i>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - 韩国漫画封面`}
                      title={`${comic.title} - 免费韩国漫画在线阅读`}
                      className="w-full h-full object-cover object-top"
                      onError={() => setImgError(true)}
                    />
                  )}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {comic.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-600">分类：
                    <Link to={`/category/${comic.categorySlug}`} className="text-primary-600 hover:text-primary-700">{comic.category}</Link>
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className={`text-sm font-medium ${comic.status === '连载中' ? 'text-primary-600' : 'text-secondary-600'}`}>
                    {comic.status}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-0.5 rounded-full text-xs bg-accent-100 text-accent-700 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <span className="text-accent-500 font-bold text-lg">{comic.rating}</span>
                    <span className="text-foreground-500">分</span>
                  </div>
                  <span className="text-foreground-300">|</span>
                  <span className="text-foreground-600">
                    <span className="inline-flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-eye-line"></i>
                      </div>
                      {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views} 次阅读
                    </span>
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-foreground-600">
                    <span className="inline-flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-heart-line"></i>
                      </div>
                      {comic.followers >= 10000 ? `${(comic.followers / 10000).toFixed(0)}万` : comic.followers} 收藏
                    </span>
                  </span>
                </div>

                <div className="mt-5 bg-background-100 rounded-lg p-4">
                  <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    开始阅读 第{comic.chapters.length}话
                  </button>
                  <button className="px-8 py-3 border border-background-300 text-foreground-700 rounded-full text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                    加入收藏
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">章节列表</h2>
              <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <div className="divide-y divide-background-100">
                  {comic.chapters.map((chapter, index) => (
                    <div
                      key={chapter.id}
                      className="flex items-center justify-between px-4 py-3.5 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-foreground-400 w-8">{index + 1}</span>
                        <span className="text-sm font-medium text-foreground-900">{chapter.title}</span>
                      </div>
                      <span className="text-xs text-foreground-400">{chapter.updateDate}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {relatedComics.length > 0 && (
              <div className="mt-12">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {relatedComics.map((rc) => (
                    <RelatedComicCard key={rc.id} comic={rc} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}